import React, { FunctionComponent, useState } from 'react';
import styled from 'styled-components';
import { isEmpty } from 'ts-utils';
import { CopyContainer } from '../../styles/CopyContainer';
import { BodySM, Heading3 } from '../../styles/Typography';

export type DropdownOption =
  | string
  | {
      value: string;
      label: string;
      disabled?: boolean;
    };

export type DropdownSelectLayoutConfig = {
  heading?: string;
  question: string;
  options: DropdownOption[];
  placeholder?: string;
};

type Props = DropdownSelectLayoutConfig & {
  onSelected?: (value: string) => void;
};

export const DropdownSelectLayout: FunctionComponent<Props> = (props) => {
  const { heading, question, options, placeholder = 'Select an option...', onSelected } = props;

  const [selectedValue, setSelectedValue] = useState<string>('');

  function onSelectionChange(value: string) {
    setSelectedValue(value);
    onSelected?.(value);
  }

  return (
    <ContentContainer>
      <CopyContainer>
        {!isEmpty(heading) && <Heading>{heading}</Heading>}
        <BodySM data-test="surveyQuestion">{question}</BodySM>
      </CopyContainer>

      <StyledSelect
        value={selectedValue}
        onChange={(e) => onSelectionChange(e.target.value)}
        data-test="dropdown-select"
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((option, index) => {
          const { value, label, disabled } =
            typeof option === 'object' ? option : { value: option, label: option, disabled: false };

          return (
            <option key={index} value={value} disabled={disabled}>
              {label}
            </option>
          );
        })}
      </StyledSelect>
    </ContentContainer>
  );
};

const Heading = styled(Heading3)`
  margin-bottom: ${({ theme }) => theme.spacing._16px};
`;

const ContentContainer = styled.div`
  margin: auto;
  max-width: 600px;
`;

// Chevron drawn as a background so its inset from the right edge is ours to control; the native
// menulist indicator hugs the border regardless of padding.
const CHEVRON_DOWN_SVG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="%23333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>';

const StyledSelect = styled.select`
  width: 100%;
  padding: ${({ theme }) =>
    `${theme.spacing._12px} ${theme.spacing._48px} ${theme.spacing._12px} ${theme.spacing._12px}`};
  font-size: 16px;
  border-radius: 8px;
  border: 1px solid #ccc;
  margin-top: 16px;
  margin-bottom: 24px;
  appearance: none;
  -webkit-appearance: none;
  background-color: white;
  background-image: url('${CHEVRON_DOWN_SVG}');
  background-repeat: no-repeat;
  background-position: right ${({ theme }) => theme.spacing._12px} center;
`;
